/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "../ui/sonner";
import { Check, X, CalendarClock, Loader2 } from "lucide-react";

export const INVITE_STATUS = {
  pending: ["待回复", "Pending", "bg-amber-100 text-amber-700"],
  accepted: ["已同意", "Accepted", "bg-emerald-100 text-emerald-700"],
  declined: ["已拒绝", "Declined", "bg-red-100 text-red-700"],
  reschedule: ["申请改期", "Reschedule", "bg-sky-100 text-sky-700"],
};

export function InviteStatusBadge({ status, zh, testid }) {
  const m = INVITE_STATUS[status] || INVITE_STATUS.pending;
  return <span data-testid={testid} data-status={status} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${m[2]}`}>{zh ? m[0] : m[1]}</span>;
}

export const fmtAt = (iso) => { try { return iso ? new Date(iso).toLocaleString("zh-CN", { hour12: false, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""; } catch { return ""; } };

/* 同意 / 拒绝 / 改期 (拒绝、改期需原因; 改期需建议日期时间) → POST respond */
export function InviteRespond({ api, zh, leadId, invite, onDone, compact = false }) {
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [pd, setPd] = useState("");
  const [pt, setPt] = useState("");
  const [busy, setBusy] = useState(false);
  const iid = invite.id;
  const submit = async (action) => {
    if (action !== "accept" && !reason.trim()) { toast.error(zh ? "请填写原因" : "Reason required"); return; }
    if (action === "reschedule" && (!pd || !pt)) { toast.error(zh ? "请填写建议的日期与时间" : "Proposed date & time required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/site-eval-invites/${iid}/respond`, { action, reason: reason.trim(), proposedDate: pd, proposedTime: pt });
      toast.success(action === "accept" ? (zh ? "已同意，已加入预约提醒名单" : "Accepted") : action === "decline" ? (zh ? "已拒绝，原因已留档" : "Declined") : (zh ? "已提交改期申请，发起人与销售已收到通知" : "Reschedule requested"));
      setMode(null); setReason(""); setPd(""); setPt("");
      window.dispatchEvent(new CustomEvent("eval-invites-changed"));
      onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <div className={`space-y-1.5 ${compact ? "" : "mt-1"}`} data-testid={`invite-respond-${iid}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button size="sm" className="h-6 text-[11px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || invite.status === "accepted"} onClick={() => submit("accept")} data-testid={`invite-accept-${iid}`}>
          {busy && mode === null ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-0.5" />}{zh ? "同意" : "Accept"}
        </Button>
        <Button size="sm" variant="outline" className={`h-6 text-[11px] px-2 ${mode === "decline" ? "border-red-400 text-red-700" : "text-red-600"}`} disabled={busy} onClick={() => setMode(mode === "decline" ? null : "decline")} data-testid={`invite-decline-${iid}`}><X className="h-3 w-3 mr-0.5" />{zh ? "拒绝" : "Decline"}</Button>
        <Button size="sm" variant="outline" className={`h-6 text-[11px] px-2 ${mode === "reschedule" ? "border-sky-400 text-sky-700" : "text-sky-700"}`} disabled={busy} onClick={() => setMode(mode === "reschedule" ? null : "reschedule")} data-testid={`invite-reschedule-${iid}`}><CalendarClock className="h-3 w-3 mr-0.5" />{zh ? "改期" : "Reschedule"}</Button>
        {!invite.isSelf && <span className="text-[10px] text-slate-400">{zh ? "以部门经理身份代答" : "responding as manager"}</span>}
      </div>
      {mode && (
        <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-1.5" data-testid={`invite-form-${iid}`}>
          {mode === "reschedule" && (
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              <span className="text-slate-500">{zh ? "建议改为" : "Propose"}</span>
              <input type="date" value={pd} onChange={(e) => setPd(e.target.value)} className="rounded border border-slate-200 px-1.5 py-0.5" data-testid={`invite-proposed-date-${iid}`} />
              <input type="time" value={pt} onChange={(e) => setPt(e.target.value)} className="rounded border border-slate-200 px-1.5 py-0.5" data-testid={`invite-proposed-time-${iid}`} />
            </div>
          )}
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={mode === "decline" ? (zh ? "拒绝原因（必填，留档备查）" : "Decline reason (required)") : (zh ? "改期原因（必填，留档备查）" : "Reschedule reason (required)")} className="text-xs min-h-[48px]" data-testid={`invite-reason-${iid}`} />
          <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
            <Button size="sm" className={`h-6 text-[11px] px-2 text-white ${mode === "decline" ? "bg-red-600 hover:bg-red-700" : "bg-sky-600 hover:bg-sky-700"}`} disabled={busy} onClick={() => submit(mode)} data-testid={`invite-submit-${iid}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (mode === "decline" ? (zh ? "确认拒绝" : "Confirm decline") : (zh ? "提交改期申请" : "Submit"))}
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={() => setMode(null)} data-testid={`invite-cancel-${iid}`}>{zh ? "取消" : "Cancel"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function InviteMeta({ inv, zh }) {
  return (
    <span className="text-[10px] text-slate-500" data-testid={`invite-meta-${inv.id}`}>
      {inv.departmentName ? `${inv.departmentName} · ` : ""}{inv.status !== "pending" && inv.respondedAt ? `${inv.respondedByName || inv.respondedBy || ""}${inv.byManager ? (zh ? "（经理代答）" : " (manager)") : ""} · ${fmtAt(inv.respondedAt)}` : (zh ? `邀约于 ${fmtAt(inv.invitedAt)}` : `invited ${fmtAt(inv.invitedAt)}`)}
      {inv.proposedDate ? <span className="text-sky-700"> · {zh ? "建议" : "propose"} {inv.proposedDate} {inv.proposedTime}</span> : null}
      {inv.reason ? <span className="text-slate-600"> · {zh ? "原因：" : "reason: "}{inv.reason}</span> : null}
    </span>
  );
}
